import { BOARD_COLUMNS, assigneesOf, isAssigneeOf, isLeader, isNewRequestFor } from './derive';

/**
 * Task 화면 구조 (1022 수정 1차 · ② Workboard · Task).
 *   My tasks  — 보기 탭 Assigned to me(기본) · To review. Timeline 없음
 *   All tasks — 보기 탭 Timeline(기본) · By progress · By assignee
 * 열 = { key, label, icon, match(task, me), foldable(기본 접힘) }. List 레이아웃은 같은 열을 같은 순서의 섹션으로 보여 준다.
 */

export const MY_VIEWS = [
  { key: 'assigned', label: 'Assigned to me' },
  { key: 'review', label: 'To review' },
];
export const ALL_VIEWS = [
  { key: 'timeline', label: 'Timeline' },
  { key: 'progress', label: 'By progress' },
  { key: 'assignee', label: 'By assignee' },
];

/** My tasks 에서는 rework_requested 를 "Changes requested" 로 부른다 */
export const MY_LABELS = { rework_requested: 'Changes requested' };

const mine = (t, me) => isAssigneeOf(t, me);
const toMe = (t, me) => t.receiverId === me;

export const COLUMNS = {
  assigned: [
    {
      key: 'to_accept',
      label: 'To accept',
      icon: 'todo',
      match: (t, me) => mine(t, me) && t.state === 'assigned',
    },
    {
      key: 'in_progress',
      label: 'In progress',
      icon: 'in_progress',
      match: (t, me) => mine(t, me) && ['in_progress', 'rework_requested'].includes(t.state),
    },
    {
      key: 'waiting',
      label: 'Waiting for review',
      icon: 'in_review',
      foldable: true,
      match: (t, me) => mine(t, me) && t.state === 'submitted',
    },
    {
      key: 'done',
      label: 'Done',
      icon: 'done',
      foldable: true,
      match: (t, me) => mine(t, me) && t.state === 'approved',
    },
  ],
  review: [
    {
      key: 'to_review',
      label: 'To review',
      icon: 'in_review',
      match: (t, me) => toMe(t, me) && t.state === 'submitted',
    },
    {
      key: 'sent_back',
      label: 'Sent back',
      icon: 'sent_back',
      foldable: true,
      match: (t, me) => toMe(t, me) && t.state === 'rework_requested',
    },
    {
      key: 'approved',
      label: 'Approved',
      icon: 'done',
      foldable: true,
      match: (t, me) => toMe(t, me) && t.state === 'approved',
    },
  ],
  // By progress · By assignee — 기존 4단계 (To do / In progress / In review / Done)
  progress: BOARD_COLUMNS.map((c) => ({
    key: c.key,
    label: c.label,
    icon: c.key,
    match: (t) => c.states.includes(t.state),
  })),
};
COLUMNS.assignee = COLUMNS.progress;

/** 나와 관련된 태스크 — 담당자이거나 받는 사람 (All tasks 에서 테두리 + You) */
export const isMineOrToMe = (t, me) => mine(t, me) || toMe(t, me);

/** To review 탭 개수 — 받는 사람 = 나 · 검토 대기 */
export const toReviewCount = (tasks, me) =>
  tasks.filter((t) => toMe(t, me) && t.state === 'submitted').length;

/**
 * 카드의 사람 — My tasks: To review 탭 = 핸드오프를 보낸 사람(담당자),
 * Assigned to me = 배정한 PM, 반려 건(Changes requested) = 반려한 사람(받는 사람). All tasks = 담당자.
 */
export const peopleFor = (task, view, ctx) => {
  const byId = (id) => ctx.memberById.get(id);
  if (view === 'review') return assigneesOf(task).map(byId).filter(Boolean);
  if (view === 'assigned') {
    if (task.state === 'rework_requested') return [byId(task.receiverId)].filter(Boolean);
    const pm = [...ctx.memberById.values()].find(isLeader);
    return pm ? [pm] : [];
  }
  return assigneesOf(task).map(byId).filter(Boolean);
};

/** "Requested by" (My tasks) · "Assignee" (All tasks) — List 의 사람 열 이름 */
export const personHeading = (view) =>
  view === 'assigned' || view === 'review' ? 'Requested by' : 'Assignee';

/** 정렬 — Changes requested · New request 먼저, 그다음 마감이 빠른 순 */
export const sortTasks = (tasks, ctx, due) =>
  [...tasks].sort(
    (a, b) =>
      Number(b.state === 'rework_requested') - Number(a.state === 'rework_requested') ||
      Number(isNewRequestFor(b, ctx.data.viewerId)) -
        Number(isNewRequestFor(a, ctx.data.viewerId)) ||
      due(a) - due(b),
  );

/* ── 사용자별 저장 (이 기기) ─────────────────────────────── */

const read = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    return v == null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
};
const write = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 저장이 막혀도 이번 화면에서는 그대로 쓴다
  }
};

/** Layout — Board / List (사용자별, Timeline 에는 쓰지 않는다) */
export const loadLayout = (viewerId) => {
  const v = read(`2days-layout:${viewerId}`, 'board');
  return v === 'list' ? 'list' : 'board';
};
export const saveLayout = (viewerId, layout) => write(`2days-layout:${viewerId}`, layout);

/** My tasks 보기 탭 — 예전 값(board / list 등)은 버리고 기본 탭 */
export const loadMyView = (viewerId) => {
  const v = read(`2days-view:mine:${viewerId}`, 'assigned');
  return MY_VIEWS.some((x) => x.key === v) ? v : 'assigned';
};
export const saveMyView = (viewerId, view) => write(`2days-view:mine:${viewerId}`, view);

/** 열 접힘 — { [보기:열]: true/false }. 저장된 값이 없으면 열의 기본값(foldable = 접힘) */
export const loadFolds = (viewerId) => read(`2days-folds:${viewerId}`, {});
export const saveFolds = (viewerId, folds) => write(`2days-folds:${viewerId}`, folds);
export const isFolded = (folds, view, col) => folds[`${view}:${col.key}`] ?? !!col.foldable;
